import { isEmpty, isNil, isNotNil, last } from 'ramda';

import i18n from 'i18n';
import {
  calcNumberOfNightsAdapter,
  formatNumberOfNights,
  formatTimeRangeAdapter,
  getTimeRangeAdapter,
} from 'utils/itineraryDetails';

import { SCHEDULE_ITEM_TYPES, SCHEDULE_TYPES, PAGE_MENTION_ENTITY_TYPE } from '../../utils/itineraryDetails/constants';

import { isInternalScheduleItem } from '.';

const transformDetailsEntity = (entity, index, scheduleItem) => {
  return {
    id: entity.data.id,
    type: entity.data.type,
    originalScheduleId: scheduleItem?.details?.pages?.[index]?.originalScheduleId,
  };
};
const transformAccommodationEntity = (entity, index, scheduleItem) => {
  const entityData = entity.data;

  return {
    id: entityData.id,
    type: entityData.type,
    rooms: entityData.rooms?.map((room) => ({ id: room.id })) ?? [],
    areaId: entityData.area?.id ?? null,
    originalScheduleId: scheduleItem?.accommodation?.pages?.[index]?.originalScheduleId,
  };
};

export const extractEntitiesFromBlock = (block, entityMap) => {
  return block.entityRanges
    .map((entityBlockData) => {
      const entity = entityMap[entityBlockData.key];

      return entity?.type === PAGE_MENTION_ENTITY_TYPE ? entity : null;
    })
    .filter(isNotNil);
};
export const extractEntitiesFromContentState = (contentState = {}) => {
  if (isEmpty(contentState)) {
    return [];
  }

  return contentState.blocks.map((block) => extractEntitiesFromBlock(block, contentState.entityMap)).flat();
};

export const transformFEScheduleToBEForm = (schedule, itinerary) => {
  return {
    scheduleType: schedule.type,
    filtersForDetailsTab: itinerary.filtersForDetailsTab,
    detailsMode: itinerary.detailsMode,
    schedules: schedule.items.map((item) => {
      const accommodation =
        item.type === SCHEDULE_ITEM_TYPES.ACTIVITY
          ? {
              contentState: {},
              pages: [],
              removedPageIds: [],
            }
          : {
              contentState: item.accommodation.contentState,
              pages: extractEntitiesFromContentState(item.accommodation.contentState).map((entity, index) =>
                transformAccommodationEntity(entity, index, item),
              ),
              removedPageIds: item.accommodation.removedPageIds,
            };

      return {
        id: isInternalScheduleItem(item) ? undefined : item.id,
        type: item.type,
        datesRange: item.datesRange,
        daysRange: item.daysRange,
        details: {
          contentState: item.details.contentState,
          pages: extractEntitiesFromContentState(item.details.contentState).map((entity, index) =>
            transformDetailsEntity(entity, index, item),
          ),
          removedPageIds: item.details.removedPageIds,
        },
        accommodation,
        _destroy: item._destroy,
      };
    }),
  };
};

const prepareScheduleItem = (item) => {
  return {
    datesRange: item.datesRange ?? {},
    daysRange: item.daysRange ?? {},
    details: {
      contentState: item.details?.contentState ?? {},
      pages: item.details?.pages ?? [],
      removedPageIds: [],
    },
    accommodation: {
      contentState: item.accommodation?.contentState ?? {},
      pages: item.accommodation?.pages ?? [],
      removedPageIds: [],
    },
    type: item.type,
    id: item.id,
  };
};
export const transformBEScheduleToFEForm = (schedule = {}) => {
  const travelStartDate = schedule.travelStartDate ?? null;
  const scheduleType = schedule.type ?? SCHEDULE_TYPES.DATES;

  return {
    type: scheduleType,
    travelStartDate,
    items: (schedule.items ?? []).map((item) => prepareScheduleItem(item)),
  };
};

const buildText = (entityMeta = {}) => {
  return entityMeta.name ?? '';
};
const transformBlock = (block, entityMap) => {
  if (!block) {
    return null;
  }

  const text = block?.text;
  if (isNil(text)) {
    return null;
  }

  const entityRanges = block?.entityRanges;
  if (!entityRanges || isEmpty(entityRanges)) {
    return text;
  }

  const resultTextChunks = [];

  for (let i = 0; i < entityRanges.length; i++) {
    const prevRange = entityRanges[i - 1];
    const currentRange = entityRanges[i];
    const textBefore = text.substr(
      prevRange ? prevRange.offset + prevRange.length : 0,
      prevRange ? currentRange.offset - (prevRange.offset + prevRange.length) : currentRange.offset,
    );
    const textToReplace = buildText(entityMap?.[currentRange.key]?.data);

    resultTextChunks.push(textBefore, textToReplace);
  }

  const lastEntityRange = last(entityRanges);
  const textAfterLastEntityRange = text.substr(lastEntityRange.offset + lastEntityRange.length);
  resultTextChunks.push(textAfterLastEntityRange);

  return resultTextChunks.join('');
};
export const transformContentStateToOverviewText = (contentState) => {
  if (isEmpty(contentState)) {
    return null;
  }

  return contentState.blocks.map((block) => transformBlock(block, contentState.entityMap)).join('\n');
};

export const transformBEScheduleToOverviewForm = (scheduleType, scheduleItems, params) => {
  return scheduleItems
    .filter((item) => item.type !== null)
    .map((item) => {
      const range = getTimeRangeAdapter(scheduleType, item);
      const numberOfNights = calcNumberOfNightsAdapter(scheduleType, item.type, range);

      return {
        type: item.type,
        formattedRange: formatTimeRangeAdapter(scheduleType, range, params),
        formattedNumberOfNights:
          numberOfNights === 0
            ? null
            : i18n.t('proposal_content.nights', {
                count: numberOfNights,
                defaultValue: formatNumberOfNights(numberOfNights),
              }),
        detailsText: transformContentStateToOverviewText(item.details.contentState),
        accommodationText:
          item.type === SCHEDULE_ITEM_TYPES.ACTIVITY
            ? null
            : transformContentStateToOverviewText(item.accommodation.contentState),
      };
    });
};
